import { useMemo } from 'react';

import {
  RyeconCode,
  RyeconDashboard,
  RyeconLineage,
  RyeconQuery,
  toast,
} from '@dbt-labs/sourdough';

import { OpenInMenuGroup, OpenInMenuItem } from '@/components/ActionButtons';
import { ButtonAction } from '@/pages/account/project/resource/details/components/HeaderButtons';
import {
  getName,
  NavigationResourceType,
} from '@/panel/ResourcesFilter/selectedResource';
import { getExploreUrlFromQueryUrl, openLinkInNewTab } from '@/util/dbt/navigation';
import { toHandoffAssetDbtType } from '@/util/handoffs';
import { useIdeHandoff } from './ide/handoffs';
import { useInsightsDbtHandoff } from './query/handoffs';
import { useHandoffEventProject } from './telemetry/handoffs';
import { useCanvasDbtHandoff } from './visual-editor/handoffs';

export const useOpenIn = ({
  files,
  packageName,
  exposureUrl,
  resourceType,
  uniqueId,
  projectId,
  environmentId,
}: {
  files: (string | null | undefined)[];
  packageName: string | null | undefined;
  resourceType: NavigationResourceType | 'package';
  exposureUrl?: string | null;
  uniqueId?: string;
  projectId?: number;
  environmentId?: number;
}) => {
  /**
   * WARNING: Since dbt resources page will be rendered via a project-based url
   * or the url of a catalog that is also a dbt resource (META-4725), we are no
   * longer guaranteed to be in a project context.
   */
  const { sendHandoffEventProject } = useHandoffEventProject({
    projectId,
    environmentId,
  });
  const { showIdeLink, ideLink, disableIdeLink, determiningIdeAccess } = useIdeHandoff({
    files,
    packageName,
    exposureUrl,
    projectId,
  });
  const { showInsightsLink, insightsHandoffLink, determiningInsightsAccess } =
    useInsightsDbtHandoff({
      resourceType,
      uniqueId: uniqueId || '',
      projectId,
      environmentId,
    });
  const {
    showCanvasCreateLink,
    canvasCreateLink,
    showCanvasEditLink,
    canvasEditLink,
    determiningCanvasAccess,
  } = useCanvasDbtHandoff({
    resourceType,
    uniqueId: uniqueId || '',
    packageName,
    projectId,
    environmentId,
  });

  const showCanvasAction: boolean =
    (showCanvasCreateLink || showCanvasEditLink) && Boolean(uniqueId);

  const fetchingItems =
    determiningIdeAccess || determiningInsightsAccess || determiningCanvasAccess;

  const openInItems: (OpenInMenuItem | OpenInMenuGroup)[] = useMemo(
    () => [
      {
        label: 'Insights',
        ryecon: RyeconQuery,
        show: showInsightsLink,
        items: [
          {
            label: 'Analyze data',
            value: 'query' as ButtonAction,
          },
        ],
      },
      {
        label: 'Canvas',
        ryecon: RyeconLineage,
        show: showCanvasAction,
        includeTopDivider: true,
        items: [
          ...(showCanvasEditLink
            ? [
                {
                  label: `Edit ${getName(resourceType)}`,
                  value: 'canvas-edit' as ButtonAction,
                },
              ]
            : []),
          ...(showCanvasCreateLink
            ? [
                {
                  label: 'Create downstream model',
                  value: 'canvas-create' as ButtonAction,
                },
              ]
            : []),
        ],
      },
      {
        label: 'Studio',
        ryecon: RyeconCode,
        show: showIdeLink && !disableIdeLink,
        includeTopDivider: true,
        items: [
          {
            label: `Edit ${getName(resourceType)}`,
            value: 'ide' as ButtonAction,
          },
        ],
      },
      {
        label: 'Dashboard',
        value: 'exposure-dashboard' as ButtonAction,
        includeTopDivider: true,
        ryecon: RyeconDashboard,
        show: !!exposureUrl,
      },
    ],
    [
      showInsightsLink,
      showCanvasAction,
      showCanvasEditLink,
      resourceType,
      showCanvasCreateLink,
      showIdeLink,
      disableIdeLink,
      exposureUrl,
    ],
  );

  const resolverMapping = useMemo(() => {
    const assetType = toHandoffAssetDbtType(resourceType);
    return {
      explorer: () => {
        openLinkInNewTab(getExploreUrlFromQueryUrl(window.location.href));
      },
      ide: () => {
        sendHandoffEventProject({
          handoff_to: 'ide',
          is_final_handoff_action: true,
          asset_type: assetType,
        });
        openLinkInNewTab(ideLink);
      },
      query: () => {
        sendHandoffEventProject({
          handoff_to: 'insights',
          is_final_handoff_action: true,
          asset_type: assetType,
        });
        openLinkInNewTab(insightsHandoffLink);
      },
      'canvas-create': () => {
        sendHandoffEventProject({
          handoff_to: 'canvas',
          is_final_handoff_action: true,
          asset_type: assetType,
          handoff_action: 'create',
        });
        openLinkInNewTab(canvasCreateLink);
      },
      'canvas-edit': () => {
        sendHandoffEventProject({
          handoff_to: 'canvas',
          is_final_handoff_action: true,
          asset_type: assetType,
          handoff_action: 'edit',
        });
        openLinkInNewTab(canvasEditLink);
      },
      'exposure-dashboard': () => {
        if (!exposureUrl) return;
        openLinkInNewTab(exposureUrl);
      },
      share: () => {
        navigator.clipboard.writeText(window.location.href);
        toast.success('Copied link to clipboard', { id: 'copy-link' });
      },
    };
  }, [
    resourceType,
    sendHandoffEventProject,
    ideLink,
    insightsHandoffLink,
    canvasCreateLink,
    canvasEditLink,
    exposureUrl,
  ]);

  return {
    actionResolvers: resolverMapping,
    openInItems,
    isFetching: fetchingItems,
  };
};
